import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@tickitacka/shared';
import { reportService } from '../services/reportService';
import { ReportWithDetails } from '../types/reportTypes';
import {
    useReportsByUserId,
    useReportsWithReceiverByUserId,
} from '../services/useReportQueries';
import { useSidebarUnreadStore } from '@/stores/sidebarUnreadStore';


/**
 * 보고서 데이터를 실시간으로 스트리밍하는 훅
 * @param reportId 보고서 ID
 * @returns 실시간으로 업데이트되는 보고서 데이터, 로딩 상태
 */
export function useStreamReport(reportId: string | undefined) {
    const [report, setReport] = useState<ReportWithDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const queryClient = useQueryClient();

    // 보고서 조회 쿼리 훅 사용 (캐시 관리를 위해)
    const { data: cachedReport } = useQuery<ReportWithDetails | null>({
        queryKey: ['report', reportId],
        queryFn: () =>
            reportId
                ? reportService.getReportById(reportId)
                : Promise.resolve(null),
        enabled: !!reportId,
        staleTime: 1000 * 60 * 5, // 5분 동안 캐시 유지
    });

    // 초기 데이터 설정
    useEffect(() => {
        if (cachedReport) {
            setReport(cachedReport);
            setIsLoading(false);
        }
    }, [cachedReport]);

    // Supabase 실시간 구독 설정
    useEffect(() => {
        if (!reportId) {
            setReport(null);
            setIsLoading(false);
            return;
        }

        // 초기 데이터가 없을 때만 로딩 상태 표시
        if (!cachedReport) {
            setIsLoading(true);
        }

        // 디바운스 처리 로직
        let debounceTimer: NodeJS.Timeout | null = null;

        const debouncedInvalidate = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                console.log('디바운스 타이머 실행 - 보고서 업데이트');
                // 보고서 쿼리 무효화
                void queryClient.invalidateQueries({
                    queryKey: ['report', reportId],
                });

                // 보고서 목록 쿼리 무효화
                void queryClient.invalidateQueries({
                    queryKey: ['reports'],
                });
            }, 300); // 300ms 디바운스
        };

        // Supabase 채널 설정
        const channel = supabase.channel(`report-${reportId}-realtime`);

        // UPDATE 이벤트 처리 - 보고서 수정
        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'report',
                filter: `id=eq.${reportId}`,
            },
            (payload: {
                old: Record<string, unknown>;
                new: Record<string, unknown>;
            }) => {
                console.log('보고서 수정 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // DELETE 이벤트 감지 - 보고서 삭제 (소프트 삭제 포함)
        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'report',
                filter: `id=eq.${reportId} AND enable=eq.false`,
            },
            (payload: {
                old: Record<string, unknown>;
                new: Record<string, unknown>;
            }) => {
                console.log('보고서 삭제 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // 수신자 변경 이벤트 감지
        channel.on(
            'postgres_changes',
            {
                event: '*', // INSERT, UPDATE, DELETE 모두 감지
                schema: 'public',
                table: 'report_receiver',
                filter: `report_id=eq.${reportId}`,
            },
            (payload: {
                old: Record<string, unknown>;
                new: Record<string, unknown>;
            }) => {
                console.log('보고서 수신자 변경 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // Supabase 구독 시작
        const subscription = channel.subscribe((status: string) => {
            if (status === 'SUBSCRIBED') {
                console.log(`Subscribed to report-${reportId}-realtime`);
            } else if (status === 'CHANNEL_ERROR') {
                console.error(
                    `Error subscribing to report-${reportId}-realtime`
                );
            }
        });

        // 클린업 함수
        return () => {
            try {
                void subscription.unsubscribe();
            } catch (error: unknown) {
                console.error(
                    '구독 해제 중 오류 발생:',
                    error instanceof Error ? error.message : '알 수 없는 오류'
                );
            }
        };
    }, [reportId, queryClient, cachedReport]);

    return { report, isLoading };
}

/**
 * 보고서 수신 목록을 실시간으로 스트리밍하는 훅
 * @param userId 사용자 ID
 * @param workspaceId 선택적 워크스페이스 ID
 * @param searchQuery 검색어 (선택사항)
 * @returns 실시간으로 업데이트되는 보낸 보고서 목록, 받은 보고서 목록, 로딩 상태
 */
export function useStreamReportList(
    userId: string | undefined,
    workspaceId?: string,
    searchQuery?: string
) {
    const [sentReports, setSentReports] = useState<ReportWithDetails[]>([]);
    const [receivedReports, setReceivedReports] = useState<ReportWithDetails[]>(
        []
    );
    const [isLoading, setIsLoading] = useState(true);
    const queryClient = useQueryClient();

    // 기존 훅을 재사용하여 보낸 보고서 데이터 가져오기
    const { data: cachedSentReports } = useReportsByUserId(
        userId,
        workspaceId, // 워크스페이스 ID 전달
        searchQuery, // 검색어 전달
        {
            staleTime: 1000 * 60 * 5, // 5분 동안 캐시 유지
        }
    );

    // 기존 훅을 재사용하여 받은 보고서 데이터 가져오기
    const { data: cachedReceivedReports } = useReportsWithReceiverByUserId(
        userId,
        workspaceId, // 워크스페이스 ID 전달
        searchQuery, // 검색어 전달
        {
            staleTime: 1000 * 60 * 5, // 5분 동안 캐시 유지
        }
    );

    // 초기 데이터 설정
    useEffect(() => {
        if (cachedSentReports) {
            setSentReports(cachedSentReports);
        }
        if (cachedReceivedReports) {
            setReceivedReports(cachedReceivedReports);
        }
        if (cachedSentReports || cachedReceivedReports) {
            setIsLoading(false);
        }
    }, [cachedSentReports, cachedReceivedReports]);

    // Supabase 실시간 구독 설정
    // 리렌더링될 때 타이머가 초기화되는 문제 방지하기 위해 useEffect 외부에서 타이머 관리
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!userId) {
            setSentReports([]);
            setReceivedReports([]);
            setIsLoading(false);
            return;
        }

        // 초기 데이터가 없을 때만 로딩 상태 표시
        if (!cachedSentReports && !cachedReceivedReports) {
            setIsLoading(true);
        }

        // 디바운스 처리 함수 정의
        const debouncedInvalidate = () => {
            if (debounceTimerRef.current)
                clearTimeout(debounceTimerRef.current);

            debounceTimerRef.current = setTimeout(() => {
                console.log('디바운스 타이머 실행 - 보고서 목록 업데이트');
                // 보낸 보고서 쿼리 무효화
                void queryClient.invalidateQueries({
                    queryKey: ['reports', 'user_with_receivers', userId],
                });

                // 받은 보고서 쿼리 무효화 - 수신자 정보 포함 버전도 무효화
                void queryClient.invalidateQueries({
                    queryKey: ['reports', 'receiver_with_details', userId],
                });
            }, 300); // 300ms 디바운스
        };

        // Supabase 채널 설정
        const channel = supabase.channel(`reports-${userId}-realtime`);

        // 보고서 수정 이벤트 감지
        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'report_receiver',
            },
            (payload: {
                old: Record<string, unknown>;
                new: Record<string, unknown>;
            }) => {
                console.log('보고서 수정 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // 보고서 삭제 이벤트 감지 (소프트 삭제 포함)
        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'report_receiver',
                filter: `user_id=eq.${userId}`,
            },
            (payload: {
                old: Record<string, unknown>;
                new: Record<string, unknown>;
            }) => {
                console.log('보고서 삭제 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // 새 보고서 이벤트 감지
        channel.on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'report',
            },
            (payload: { new: Record<string, unknown> }) => {
                console.log('새 보고서 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // 수신자 변경 이벤트 감지
        channel.on(
            'postgres_changes',
            {
                event: '*', // INSERT, UPDATE, DELETE 모두 감지
                schema: 'public',
                table: 'report_receiver',
                filter: `user_id=eq.${userId}`, // 현재 사용자와 관련된 변경만 감지
            },
            (payload: {
                old: Record<string, unknown>;
                new: Record<string, unknown>;
            }) => {
                console.log('보고서 수신자 변경 이벤트 감지:', payload);
                void debouncedInvalidate();
            }
        );

        // Supabase 구독 시작
        const subscription = channel.subscribe((status: string) => {
            if (status === 'SUBSCRIBED') {
                console.log(`Subscribed to reports-${userId}-realtime`);
            } else if (status === 'CHANNEL_ERROR') {
                console.error(
                    `Error subscribing to reports-${userId}-realtime`
                );
            }
        });

        // 클린업 함수
        return () => {
            try {
                void subscription.unsubscribe();
            } catch (error: unknown) {
                console.error(
                    '구독 해제 중 오류 발생:',
                    error instanceof Error ? error.message : '알 수 없는 오류'
                );
            }
        };
    }, [userId, queryClient, cachedSentReports, cachedReceivedReports]);

    return { sentReports, receivedReports, isLoading };
}

/**
 * 사이드바 읽지 않은 보고서 실시간 구독 훅
 *
 * ⚠️ 주의: 이 훅은 상태를 반환하지 않습니다!
 * - 실시간 구독만 담당하고 전역 상태(useSidebarUnreadStore)를 업데이트
 * - 실제 상태는 WorkspaceItem에서 useSidebarUnreadStore()로 가져옴
 *
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID
 */
export function useSubscribeToUnreadReports(
    userId: string | undefined,
    workspaceId: string | undefined
) {
    const { setUnreadReportCount } = useSidebarUnreadStore();

    useEffect(() => {
        if (!userId || !workspaceId) return;

        const checkUnreadReports = async () => {
            if (!userId || !workspaceId) return;

            try {
                // report_receiver와 report를 조인해서 워크스페이스 필터링
                const { count, error } = await supabase
                    .from('report_receiver')
                    .select('*, report!inner(workspace_id)', {
                        count: 'exact',
                        head: true,
                    })
                    .eq('user_id', userId)
                    .eq('enable', true)
                    .eq('is_read', false)
                    .eq('report.workspace_id', workspaceId); // 조인된 report 테이블의 workspace_id로 필터링

                if (error) {
                    console.error('읽지 않은 보고서 조회 실패', error);
                    return; // 에러 시 이전 상태 유지하도록 리턴
                }

                setUnreadReportCount(count || 0);
            } catch (error) {
                console.error('[네트워크 에러] 보고서 구독 실패:', error);
                return;
            }
        };

        void checkUnreadReports();

        // 실시간 구독 - report_receiver 테이블 변경 감지
        const channel = supabase.channel(
            `unread-reports-${userId}-${workspaceId}`
        );

        channel.on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'report_receiver',
                filter: `user_id=eq.${userId}`,
            },
            () => {
                void checkUnreadReports();
            }
        );

        channel.subscribe((status, error) => {
            // console.log('보고서 채널 구독 상태', status);
            if (error) {
                console.error('보고서 채널 구독 오류', error);
            }
        });

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [userId, workspaceId, setUnreadReportCount]);
}
